import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, TrendingUp, MapPin, Crown, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { useBrand } from "@/config/brands";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";

interface WorstHospitalOffender {
  hospital_id: string;
  avg_gross_markup_percentage: number;
  // Median-baseline markup — the metric overall_rank_gross is ordered by.
  median_gross_markup_percentage?: number | null;
  expensive_procedures_count: number;
  outlier_expensive_count: number;
  shame_score_calculated: number;
  overall_rank_gross: number;
  total_procedures: number;
  hospital_name: string;
  hospital_city?: string;
  hospital_state?: string;
}

const ITEMS_PER_PAGE = 25;

const WorstHospitalOffenders = () => {
  const brand = useBrand();
  const [currentPage, setCurrentPage] = useState(1);

  // Query to get worst hospital offenders with highest markup percentages
  const { data: offenders, isLoading } = useQuery({
    queryKey: ['worst-hospital-offenders', currentPage],
    queryFn: async () => {
      // Get hospitals with highest markup percentages from hospital_ranking_summary
      const { data: fallbackData, error: fallbackError } = await supabase
          .from('hospital_ranking_summary')
          .select(`
            hospital_id,
            avg_gross_markup_percentage,
            median_gross_markup_percentage,
            expensive_procedures_count,
            outlier_expensive_count,
            shame_score_calculated,
            overall_rank_gross,
            total_procedures
          `)
          .not('avg_gross_markup_percentage', 'is', null)
          // Honor the SAME ranking-eligibility gate the canonical /hospital-
          // rankings use (cornerstone_pass AND coverage >= 50, materialized as
          // exclude_from_rankings). Without this, hospitals with a handful of
          // priced procedures — e.g. Kaiser Permanente California with just 3 —
          // surfaced as the #1 "Extreme" offender at 4,191%: a statistically
          // meaningless sample presented as the worst gouger in America
          // (journalist/defamation risk, launch audit 2026-07-13).
          .or('exclude_from_rankings.is.null,exclude_from_rankings.eq.false')
          // Rank order = median-baseline markup order, so the displayed
          // overall_rank_gross labels ascend 1, 2, 3… down the page.
          .order('median_gross_markup_percentage', { ascending: false, nullsFirst: false })
          .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

        if (fallbackError) throw fallbackError;

        // Get hospital details separately to avoid relationship issues
        const hospitalIds = fallbackData?.map(item => item.hospital_id) || [];
        const { data: hospitalsData } = hospitalIds.length > 0
          ? await supabase
              .from('hospitals')
              .select('id, name, city, state')
              .in('id', hospitalIds)
          : { data: [] };

        const hospitalsMap = new Map(hospitalsData?.map(h => [h.id, h] as const) || []);

        return fallbackData?.map(item => {
          const hospital = hospitalsMap.get(item.hospital_id) as { id: string; name: string; city?: string; state?: string; } | undefined;
          return {
            hospital_id: item.hospital_id,
            avg_gross_markup_percentage: item.avg_gross_markup_percentage,
            median_gross_markup_percentage: (item as any).median_gross_markup_percentage,
            expensive_procedures_count: item.expensive_procedures_count,
            outlier_expensive_count: item.outlier_expensive_count,
            shame_score_calculated: item.shame_score_calculated,
            overall_rank_gross: item.overall_rank_gross,
            total_procedures: item.total_procedures,
            hospital_name: hospital?.name || 'Unknown Hospital',
            hospital_city: hospital?.city,
            hospital_state: hospital?.state,
          };
        }) || [];
    },
  });

  // Get total count for pagination
  const { data: totalCount } = useQuery({
    queryKey: ['worst-hospital-offenders-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('hospital_ranking_summary')
        .select('*', { count: 'exact', head: true })
        .not('avg_gross_markup_percentage', 'is', null)
        // Match the list query's eligibility gate so pagination counts agree.
        .or('exclude_from_rankings.is.null,exclude_from_rankings.eq.false');

      if (error) throw error;
      return count || 0;
    },
  });

  const totalPages = Math.ceil((totalCount || 0) / ITEMS_PER_PAGE);

  const getBadgeVariant = (rank: number) => {
    if (rank === 1) return "destructive";
    if (rank <= 5) return "secondary";
    if (rank <= 10) return "outline";
    return "default";
  };

  const getMarkupBadge = (markup: number) => {
    if (markup >= 500) return { variant: "destructive" as const, label: "Extreme" };
    if (markup >= 300) return { variant: "secondary" as const, label: "Very High" };
    if (markup >= 200) return { variant: "outline" as const, label: "High" };
    return { variant: "default" as const, label: "Moderate" };
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading worst hospital offenders...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Worst Hospital Offenders — Highest Markup Rankings | {brand.name}</title>
        <meta name="description" content="The hospitals with the highest price markups in America, ranked by shame score. Compare average markup percentages and find which hospitals charge patients the most." />
        <meta name="keywords" content="worst hospitals, highest hospital prices, hospital markup ranking, hospital shame score, price gouging hospitals" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/worst-hospital-offenders`} />
        <meta property="og:title" content="Worst Hospital Offenders — Highest Markup Rankings" />
        <meta property="og:description" content="The hospitals with the highest price markups in America, ranked by shame score. Compare average markup percentages and find which hospitals charge patients the most." />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/worst-hospital-offenders`} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Worst Hospital Offenders — Highest Markup Rankings" />
        <meta name="twitter:description" content="The hospitals with the highest price markups in America, ranked by shame score. Compare average markup percentages and find which hospitals charge patients the most." />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Worst Hospital Offenders
              </h1>
              <p className="text-lg text-gray-600">
                Hospitals with the highest average markup percentages - the worst price gouging in healthcare
              </p>
            </div>
          </div>

          {/* View All Button */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalCount || 0)} of {totalCount || 0} hospitals
            </div>
            <Button asChild variant="outline">
              <Link to="/hospital-rankings" className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                View All Hospital Rankings
              </Link>
            </Button>
          </div>
        </div>

        {/* Offenders Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-red-600" />
              {brand.key === 'ahp' ? 'Highest Markup Rankings' : 'Hall of Shame Rankings'}
            </CardTitle>
            <CardDescription>
              Hospitals ranked by average markup percentage across all procedures
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Rank</TableHead>
                    <TableHead>Hospital</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Markup vs National</TableHead>
                    <TableHead className="text-right">Total Procedures</TableHead>
                    <TableHead className="text-right">Expensive Count</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {offenders?.map((hospital, index) => {
                    const rank = hospital.overall_rank_gross; // Use actual database ranking instead of sequential
                    // Rank is ordered by median-baseline markup — display that
                    // metric so #1, #2, #3… descend cleanly down the column.
                    const rowMarkup = hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage ?? 0;
                    const markupBadge = getMarkupBadge(rowMarkup);
                    
                    return (
                      <TableRow key={hospital.hospital_id} className="hover:bg-gray-50">
                        <TableCell>
                          <Badge variant={getBadgeVariant(rank)} className="font-mono">
                            #{rank}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900">
                              {hospital.hospital_name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="h-3 w-3" />
                            {hospital.hospital_city && hospital.hospital_state 
                              ? `${hospital.hospital_city}, ${hospital.hospital_state}`
                              : 'Location not available'
                            }
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Badge variant={markupBadge.variant} className="font-mono">
                              {markupBadge.label}
                            </Badge>
                            <span className="font-mono font-semibold text-red-600">
                              {rowMarkup.toFixed(1)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {hospital.total_procedures?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <TrendingUp className="h-4 w-4 text-red-500" />
                            <span className="font-mono">
                              {hospital.expensive_procedures_count?.toLocaleString() || 0}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/hospital/${hospital.hospital_id}`}>
                              View Details
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex justify-center">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    
                    {/* Page numbers */}
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum = i + 1;
                      
                      // Adjust page numbers to show around current page
                      if (totalPages > 5) {
                        if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                      }
                      
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={() => setCurrentPage(pageNum)}
                            isActive={pageNum === currentPage}
                            className="cursor-pointer"
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Worst Offender
              </CardTitle>
            </CardHeader>
            <CardContent>
              {offenders && offenders.length > 0 && currentPage === 1 && (
                <div>
                  <div className="font-semibold text-gray-900">{offenders[0].hospital_name}</div>
                  <div className="text-sm text-gray-600">
                    {offenders[0].hospital_city}, {offenders[0].hospital_state}
                  </div>
                  <div className="text-2xl font-bold text-red-600 mt-2">
                    {offenders[0].avg_gross_markup_percentage?.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">Average markup</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Total Hospitals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{totalCount?.toLocaleString()}</div>
              <div className="text-sm text-gray-500">In our database</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Average Markup</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {offenders && offenders.length > 0 
                  ? (offenders.reduce((sum, h) => sum + (h.avg_gross_markup_percentage || 0), 0) / offenders.length).toFixed(1)
                  : '0'
                }%
              </div>
              <div className="text-sm text-gray-500">This page</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default WorstHospitalOffenders;